import { Delete, DriveFolderUpload } from '@mui/icons-material';
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormLabel,
    LinearProgress,
    Tooltip,
    Typography
} from '@mui/material';
import { green, grey, red } from '@mui/material/colors';
import { observer } from 'mobx-react';
import { enqueueSnackbar } from 'notistack';
// @ts-nocheck
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useDropzone } from 'react-dropzone';

import ArquivoUtil from '../../util/ArquivoUtil';

/*
Parâmetros
*/
const TAMANHO_MAXIMO_ARQUIVO_VIDEO_EM_MEGABYTES = 200;
const TAMANHO_MAXIMO_ARQUIVO_AUDIO_EM_MEGABYTES = 10;

/*
Conversões
*/
const TAMANHO_MAXIMO_ARQUIVO_VIDEO_EM_BYTES = ArquivoUtil.mbParaBytes(TAMANHO_MAXIMO_ARQUIVO_VIDEO_EM_MEGABYTES);
const TAMANHO_MAXIMO_ARQUIVO_AUDIO_EM_BYTES = ArquivoUtil.mbParaBytes(TAMANHO_MAXIMO_ARQUIVO_AUDIO_EM_MEGABYTES);

const mapeamentoErros = {
    'file-invalid-type': 'O formato do arquivo [nome-arquivo] não pode ser enviado.',
    'file-too-large': `O arquivo [nome-arquivo] é maior que o tamanho máximo permitido.`
};

const formatosAudio = {
    'audio/mpeg': ['.mp3'],
    'audio/wav': ['.wav'],
    'audio/ogg': ['.ogg', '.opus'],
    'audio/x-m4a': ['.m4a']
};

const formatosVideo = {
    'video/x-msvideo': ['.avi'],
    'video/mp4': ['.mp4'],
    'video/3gpp': ['.3gp']
};

const UploadArquivo = observer(function UploadArquivo({ onUploadDocumento }) {
    const [isFazendoUpload, setIsFazendoUpload] = useState(false);
    const [arquivos, setArquivos] = useState([]);

    const [aceiteLgpd, setAceiteLgpd] = useState(false);
    const [exibirErroAceiteLgpd, setExibirErroAceiteLgpd] = useState(false);
    const mensagemAceiteLgpd = 'É necessário confirmar a ciência das regras de tratamento de dados (LGPD).';

    const handleChangeAceiteLgpd = (event) => {
        setAceiteLgpd(event.target.checked);

        if (event.target.checked) {
            setExibirErroAceiteLgpd(false);
        }
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept: {
            ...formatosAudio,
            ...formatosVideo
        },
        onDrop: async (arquivosAceitos, arquivosComErro) => {
            const arquivosValidados = await handleValidarArquivos(arquivosAceitos);

            const novosArquivos = arquivosValidados.map((arquivo) => ({
                arquivo,
                progresso: 0,
                erro: false,
                sucesso: false
            }));

            setArquivos((previousState) => [...previousState, ...novosArquivos]);

            handleArquivosComErro(arquivosComErro);
        }
    });

    const handleValidarArquivos = (arquivos) => {
        const arquivosValidados = [];

        for (const arquivo of arquivos) {
            let tamanhoMaximoArquivo = 0;

            if (Object.keys(formatosVideo).includes(arquivo.type)) {
                tamanhoMaximoArquivo = TAMANHO_MAXIMO_ARQUIVO_VIDEO_EM_BYTES;
            } else if (Object.keys(formatosAudio).includes(arquivo.type)) {
                tamanhoMaximoArquivo = TAMANHO_MAXIMO_ARQUIVO_AUDIO_EM_BYTES;
            }

            const tamanhoArquivo = arquivo.size;

            if (tamanhoArquivo > tamanhoMaximoArquivo) {
                handleArquivosComErro([{ file: arquivo, errors: [{ code: 'file-too-large' }] }]);
            } else {
                arquivosValidados.push(arquivo);
            }
        }
        return arquivosValidados;
    };

    // eslint-disable-next-line unicorn/consistent-function-scoping
    const handleArquivosComErro = (arquivosComErro) => {
        for (const arquivoComErro of arquivosComErro) {
            const nomeArquivo = arquivoComErro.file.name;

            for (const erro of arquivoComErro.errors) {
                const codigoErro = erro.code;
                const existeMapeamento = Object.hasOwn(mapeamentoErros, codigoErro);

                if (!existeMapeamento) {
                    console.error(`Mensagem de erro não mapeada: ${codigoErro}`);
                }

                const modeloMensagem = existeMapeamento
                    ? mapeamentoErros[codigoErro]
                    : 'Ocorreu um erro ao enviar o arquivo [nome-arquivo].';

                // Passar uma função callback no replace previne erros de segurança com caracteres especiais
                const mensagemErro = modeloMensagem.replace('[nome-arquivo]', () => nomeArquivo);

                enqueueSnackbar({
                    variant: 'warning',
                    message: mensagemErro
                });
            }
        }
    };

    const handleRemoverArquivo = (indiceParaRemover) => {
        setArquivos((previousState) => previousState.filter((_, indice) => indice !== indiceParaRemover));
    };

    const handleProgresso = (indice, novoProgresso) => {
        setArquivos((listaArquivos) =>
            listaArquivos.map((arquivo, indiceProgresso) =>
                indiceProgresso === indice ? { ...arquivo, progresso: novoProgresso } : arquivo
            )
        );
    };

    const atualizarStatusArquivo = (indice, status) => {
        setArquivos((listaArquivos) =>
            listaArquivos.map((arquivo, indiceProgresso) =>
                indiceProgresso === indice ? { ...arquivo, ...status } : arquivo
            )
        );
    };

    const handleUploadArquivo = async (file, indice) => {
        try {
            await onUploadDocumento(file.arquivo, (progresso) => handleProgresso(indice, progresso));
            enqueueSnackbar({
                variant: 'success',
                message: `Documento ${file.arquivo.name} foi enviado com sucesso.`
            });
            atualizarStatusArquivo(indice, { sucesso: true, erro: false });
        } catch (error) {
            console.error(error);
            atualizarStatusArquivo(indice, { sucesso: false, erro: true });
        }
    };

    const handleEnviarArquivos = async () => {
        if (aceiteLgpd === false) {
            setExibirErroAceiteLgpd(true);
            return;
        }

        setIsFazendoUpload(true);

        const uploads = arquivos
            .map((file, indice) => ({ file, indice }))
            .filter(({ indice }) => arquivos[indice].sucesso !== true)
            .map(({ file, indice }) => handleUploadArquivo(file, indice));

        await Promise.all(uploads);

        setTimeout(() => {
            setIsFazendoUpload(false);
        }, 1000);
    };

    // eslint-disable-next-line unicorn/consistent-function-scoping
    const getFormatos = (formatos) => {
        return Object.values(formatos)
            .map((value) => value.join(', '))
            .join(', ');
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Box
                {...getRootProps()}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '240px',
                    border: `1px dashed ${grey[400]}`,
                    borderRadius: 3,
                    cursor: 'pointer',
                    background: isDragActive ? grey[300] : grey[200],
                    transition: 'background 0.5s ease-in'
                }}
            >
                <input
                    data-testid="drop-input"
                    {...getInputProps()}
                />
                <DriveFolderUpload sx={{ fontSize: 40, color: grey[700] }} />
                <Typography sx={{ color: grey[800], fontSize: 14 }}>
                    Arraste e solte um arquivo ou clique para buscar
                </Typography>
                <Typography sx={{ color: grey[600], fontSize: 12 }}>
                    Apenas arquivos de áudio ({getFormatos(formatosAudio)}) de até{' '}
                    {TAMANHO_MAXIMO_ARQUIVO_AUDIO_EM_MEGABYTES}mb ou de vídeo ({getFormatos(formatosVideo)}) de até{' '}
                    {TAMANHO_MAXIMO_ARQUIVO_VIDEO_EM_MEGABYTES}mb.
                </Typography>
            </Box>
            {arquivos.length > 0 && (
                <Box sx={{ mt: 2 }}>
                    <Typography sx={{ fontWeight: 'bold', mb: 1 }}>Arquivos selecionados</Typography>

                    {arquivos.map(({ arquivo, progresso, sucesso, erro }, indice) => (
                        <Box
                            sx={{
                                padding: 2,
                                border: `1px solid ${erro ? red[900] : grey[300]}`,
                                borderRadius: 3,
                                mt: 1
                            }}
                            key={arquivo.name}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}
                            >
                                <span>{arquivo.name}</span>
                                {sucesso !== true && (
                                    <Button
                                        color="error"
                                        disabled={isFazendoUpload}
                                        onClick={() => handleRemoverArquivo(indice)}
                                    >
                                        <Delete />
                                    </Button>
                                )}
                            </Box>
                            {!erro && (
                                <LinearProgress
                                    sx={{ mt: 1 }}
                                    value={progresso}
                                    variant="determinate"
                                />
                            )}
                            {erro ? (
                                <Typography sx={{ color: red[900], mt: 1 }}>
                                    Erro ao enviar o arquivo. Tente novamente.
                                </Typography>
                            ) : null}
                            {sucesso ? (
                                <Typography sx={{ color: green[900], mt: 1 }}>Arquivo enviado com sucesso.</Typography>
                            ) : null}
                        </Box>
                    ))}

                    <Alert
                        severity="warning"
                        sx={{ mt: 3 }}
                    >
                        <AlertTitle>
                            <strong>Aviso Importante: Tratamento de Dados Audiovisuais</strong>
                        </AlertTitle>
                        <Typography
                            sx={{ mt: 1 }}
                            variant="body2"
                        >
                            Em conformidade com a Lei Geral de Proteção de Dados (LGPD) e a Resolução Conjunta CNJ/CNMP
                            nº 13/2025, torna-se ciente que:
                        </Typography>
                        <Box
                            component="ul"
                            sx={{ m: 0, pl: 2.5, mb: 1 }}
                        >
                            <li>
                                <Typography variant="body2">
                                    O conteúdo desta gravação <strong>não constitui uma peça processual</strong>.
                                </Typography>
                            </li>
                            <li>
                                <Typography variant="body2">
                                    É expressamente <strong>proibida a gravação externa</strong> da tela ou do conteúdo
                                    exibido por meio de outros dispositivos sem prévia autorização.
                                </Typography>
                            </li>
                            <li>
                                <Typography variant="body2">
                                    É <strong>vedada a edição, alteração ou realização de cortes</strong> no material
                                    original.
                                </Typography>
                            </li>
                        </Box>
                        <Typography variant="body2">
                            O descumprimento destas regras poderá acarretar sanções e impactos em processos
                            administrativos disciplinares.
                        </Typography>
                    </Alert>

                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Checkbox
                                checked={aceiteLgpd}
                                id="aceiteLgpd"
                                onChange={handleChangeAceiteLgpd}
                            />
                            <FormLabel htmlFor="aceiteLgpd">
                                Declaro que li, compreendi e concordo com as regras de tratamento de dados audiovisuais
                                descritas no aviso acima.
                            </FormLabel>
                        </Box>
                        {!!exibirErroAceiteLgpd && (
                            <Typography
                                color={red[500]}
                                sx={{ ml: 5.5, mt: 0.5 }}
                            >
                                {mensagemAceiteLgpd}
                            </Typography>
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Tooltip
                            title={
                                aceiteLgpd ? null : (
                                    <Typography>Você deve aceitar a declaração acima para prosseguir.</Typography>
                                )
                            }
                        >
                            <Box>
                                <Button
                                    disabled={isFazendoUpload || !aceiteLgpd}
                                    onClick={handleEnviarArquivos}
                                    sx={{ mt: 2 }}
                                    variant="contained"
                                >
                                    {isFazendoUpload ? (
                                        <CircularProgress
                                            size={20}
                                            sx={{ color: 'white', mr: 2 }}
                                        />
                                    ) : null}
                                    Enviar arquivos
                                </Button>
                            </Box>
                        </Tooltip>
                    </Box>
                </Box>
            )}
        </Box>
    );
});

UploadArquivo.propTypes = {
    onUploadDocumento: PropTypes.func
};

export default UploadArquivo;
